import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Header from "./Header";
import { fetchPOIs, fetchOrganizationById } from "../store/actions/organizations-action";

const PhysicalPOIs = ({ organizationId }) => {
  const dispatch = useDispatch();
  const [imageErrors, setImageErrors] = useState({});
  const [isOutdoorMedia, setIsOutdoorMedia] = useState(false);

  const poisList = useSelector(
    state => state.organizationsConfiguration.poisList
  );

  const hasError = useSelector(
    state => state.organizationsConfiguration.poisError
  );

  const isListLoaded = useSelector(
    state => state.organizationsConfiguration.poisListLoaded
  );

  const organizationDetails = useSelector(
    state => state.organizationsConfiguration.organizationDetails
  );

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchPOIs(organizationId));
      dispatch(fetchOrganizationById(organizationId));
    }
  }, [organizationId, dispatch]);

  useEffect(() => {
    if (organizationDetails && organizationDetails.subcategory) {
      setIsOutdoorMedia(organizationDetails.subcategory === 'Outdoor Media');
    }
  }, [organizationDetails]);

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px'
    },
    h1: {
      color: '#333',
      marginBottom: '30px'
    },
    error: {
      background: '#fee',
      color: '#c00',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666'
    },
    poiGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '30px',
      maxWidth: '900px',
      margin: '0 auto'
    },
    poiCard: {
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0'
    },
    poiImage: {
      width: '100%',
      objectFit: 'contain',
      background: '#e0e0e0',
      cursor: 'pointer',
      transition: 'opacity 0.3s ease'
    },
    poiImageHover: {
      opacity: '0.85'
    },
    noImage: {
      width: '100%',
      height: '500px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#e0e0e0',
      color: '#888',
      fontSize: '16px'
    },
    poiDetails: {
      padding: '25px'
    },
    poiName: {
      fontSize: '22px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '15px'
    },
    poiInfo: {
      fontSize: '15px',
      color: '#666',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px'
    },
    poiLabel: {
      fontWeight: '500',
      color: '#555',
      flexShrink: 0
    },
    poiLink: {
      color: '#007bff',
      textDecoration: 'none'
    },
    poiLinkHover: {
      textDecoration: 'underline'
    },
    count: {
      color: '#666',
      marginBottom: '20px',
      fontSize: '14px'
    },
    sectionHeader: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#68696a',
      padding: '15px 25px 10px 25px',
      borderBottom: '1px solid #e0e0e0'
    },
    sectionSeparator: {
      marginTop: '20px',
      marginBottom: '5px',
      borderTop: '1px solid #e0e0e0'
    }
  };

  const handleImageError = (poiId) => {
    setImageErrors(prev => ({ ...prev, [poiId]: true }));
  };

  const getImageContent = (poi) => {
    // Only show image section if organization is Outdoor Media
    if (!isOutdoorMedia) {
      return null;
    }

    // Check if we have an error for this image
    if (imageErrors[poi._id]) {
      return <div style={styles.noImage}>Image failed to load</div>;
    }

    if (poi.sitecard_public_url && poi.sitecard_public_url !== "null" && poi.sitecard_public_url !== null) {
      return (
        <a href={poi.sitecard_public_url} target="_blank" rel="noopener noreferrer" title="Click to view full image">
          <img
            src={poi.sitecard_public_url}
            alt={poi.name}
            style={styles.poiImage}
            onError={() => handleImageError(poi._id)}
          />
        </a>
      );
    } else if (poi.has_sitecard) {
      return <div style={styles.noImage}>Sitecard not available</div>;
    } else {
      return <div style={styles.noImage}>No Sitecard</div>;
    }
  };

  return (
    <div>
      <Header />
      <div style={styles.container}>
        <h2 style={styles.h1}>
          {poisList && poisList.length > 0 ? `POIs - ${poisList[0].organizationName}` : 'POIs'}
        </h2>
        {!isListLoaded ? (
          <div style={styles.loading}>
            <h3 style={{ color: '#666', margin: '0 0 10px 0' }}>Loading...</h3>
            <p style={{ color: '#999', margin: '0' }}>Please wait while we fetch the POIs.</p>
          </div>
        ) : hasError || (poisList && poisList.length === 0) ? (
          <div style={styles.error}>
            <h3 style={{ color: '#666', margin: '0 0 10px 0' }}>No Results Found</h3>
            <p style={{ color: '#999', margin: '0' }}>
              {hasError ? 'There was an error loading the data. Please try again.' : 'No POIs found for this organization.'}
            </p>
          </div>
        ) : (
          <>
            {poisList && poisList.length > 0 && (
              <div style={styles.count}>
                Found {poisList.length} Active POI{poisList.length !== 1 ? 's' : ''}
              </div>
            )}
            <div style={styles.poiGrid}>
              {poisList && poisList.map((poi) => (
                <div key={poi._id} style={styles.poiCard}>
                  {isOutdoorMedia && (
                    <>
                      <div style={styles.sectionHeader}>Sitecard</div>
                      {getImageContent(poi)}
                    </>
                  )}
                  {isOutdoorMedia && <div style={styles.sectionSeparator}></div>}
                  <div style={styles.sectionHeader}>POI Info</div>
                  <div style={styles.poiDetails}>
                    <div style={styles.poiName}>{poi.name}</div>

                    <div style={styles.poiInfo}>
                      <div style={styles.poiLabel}>POI ID:</div>
                      {poi._id}
                    </div>

                    <div style={styles.poiInfo}>
                      <div style={styles.poiLabel}>Organization:</div>
                      {poi.organizationName}
                    </div>

                    {isOutdoorMedia && poi.panelDimensions && typeof poi.panelDimensions === 'object' && (
                      <>
                        <div style={styles.poiInfo}>
                          <div style={styles.poiLabel}>Panel Dimensions:</div>
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Width - Pixels:</div>
                          {poi.panelDimensions.panel_width_pixels || 'null'}
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Height - Pixels:</div>
                          {poi.panelDimensions.panel_height_pixels || 'null'}
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Width - Meters (bleed):</div>
                          {poi.panelDimensions.panel_width_metres_bleed || 'null'}
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Height - Meters (bleed):</div>
                          {poi.panelDimensions.panel_height_metres_bleed || 'null'}
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Width - Meters (no bleed):</div>
                          {poi.panelDimensions.panel_width_metres_no_bleed || 'null'}
                        </div>
                        <div style={{...styles.poiInfo, marginLeft: '20px'}}>
                          <div style={styles.poiLabel}>Height - Meters (no bleed):</div>
                          {poi.panelDimensions.panel_height_metres_no_bleed || 'null'}
                        </div>
                      </>
                    )}

                    <div style={styles.poiInfo}>
                      <div style={styles.poiLabel}>Archived:</div>
                      {poi.archived ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PhysicalPOIs;
